import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { subDays } from 'date-fns'
import { isNil } from 'lodash'

import Widget from '@/components/widget/Widget'
import { FormField } from '@/components/forms/FormField'
import { PageHeader } from '@/components/headers/PageHeader'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import {
  fetchAiAnswers,
  fetchAiAnswersMetrics,
} from '@/reducers/featureFeedback/featureFeedback.actions'
import { getOrganizationOptions } from '@/utils/helpers'
import { ReviewFilesModal } from '@/views/Organizations/components/ReviewFilesModal'

import { AiAnswersStats } from './AiAnswersStats'
import { AiAnswersKnowledge } from './AiAnswersKnowledge'
import { AiAnswersVerifiedAnswersSection } from './AiAnswersVerifiedAnswersSection'
import { AiAnswersSearchLog } from './AiAnswersSearchLog'
import { canEditAgentGpt, formatDay, getEndDateParam } from './AiAnswers.helpers'

const REVIEW_CONNECTION_PARAM = 'reviewConnection'

export const AiAnswersPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const currentUser = useSelector((state) => state.currentUser)
  const { organizationid: currentUserOrgId } = currentUser
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(currentUserOrgId)
  const [reviewConnectionId, setReviewConnectionId] = useState(null)

  // Date range state with default to last 7 days
  const defaultStartDate = formatDay(subDays(new Date(), 6))
  const defaultEndDate = formatDay(new Date())
  const [dateRange, setDateRange] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  })

  const { organizations, currentOrganization } = useSelector((state) => state)
  const organizationOptions = getOrganizationOptions(organizations)
  const agentGptEnabled = currentOrganization?.agent_gpt_enabled

  const {
    loading: { aiAnswers: aiAnswersLoading, aiAnswersMetrics: metricsLoading },
    data: { aiAnswers, aiAnswersMetrics },
  } = useSelector((state) => state.featureFeedback)

  const buildParams = (range) => {
    const params = {}

    if (selectedOrganizationId) {
      params.requested_organization_id = selectedOrganizationId
    }

    if (range.startDate) {
      params.start_date = range.startDate
    }

    if (range.endDate) {
      params.end_date = getEndDateParam(range.endDate)
    }

    return params
  }

  const handleOrganizationChange = (option) => {
    setSelectedOrganizationId(option?.value ?? null)
  }

  const handleDateRangeChange = ({ selection }) => {
    if (selection.startDate && selection.endDate) {
      const startDate = formatDay(selection.startDate)
      const endDate = formatDay(selection.endDate)
      setDateRange({ startDate, endDate })
    }
  }

  useEffect(() => {
    const params = buildParams(dateRange)

    // agent-gpt derives the comparison range itself, with the same arithmetic as
    // findPreviousDateRangeFromCurrentFilters, so only the selected range is sent.
    dispatch(fetchAiAnswers(params))
    dispatch(fetchAiAnswersMetrics(params))
  }, [selectedOrganizationId, dateRange])

  // The bell's Review files link; the param stays until the modal closes, so a reload reopens it.
  // Losing the param (Back, say) closes the modal.
  useEffect(() => {
    const { [REVIEW_CONNECTION_PARAM]: requested } = queryString.parse(location.search, {
      parseNumbers: true,
    })
    setReviewConnectionId(Number.isSafeInteger(requested) && requested > 0 ? requested : null)
  }, [location.search])

  const closeReview = () => {
    setReviewConnectionId(null)
    const { [REVIEW_CONNECTION_PARAM]: closed, ...rest } = queryString.parse(location.search)
    history.replace({ pathname: location.pathname, search: queryString.stringify(rest) })
  }

  return (
    <>
      <PageHeader
        decoration="ai"
        title="AI Answers"
        description="Add the documents AgentGPT answers from, then see what your agents are searching and where the unknowns are."
      />
      {/* No label: the h1 above already names the page, so a second heading
          directly beneath it would read as two competing titles. The Upload and
          Manage cards name themselves, so the widget only carries spacing. */}
      {agentGptEnabled && canEditAgentGpt(currentUser) && (
        <Widget displayBackground={false} displayControls={false}>
          <AiAnswersKnowledge organizationId={selectedOrganizationId} />
        </Widget>
      )}
      <FilterForm small sticky={false}>
        {currentUserOrgId === 1 && (
          <OrganizationFilter
            value={selectedOrganizationId}
            options={organizationOptions}
            onChange={handleOrganizationChange}
            isClearable={false}
          />
        )}
        <FormField>
          <label>Date Range</label>
          <DateRangePickerReusable
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
            friendlyLabel
            basicLabel
            saveOrCloseOnClickPreset
          />
        </FormField>
      </FilterForm>
      <AiAnswersStats
        metrics={aiAnswersMetrics}
        dateRange={dateRange}
        metricsLoading={metricsLoading}
      />
      {agentGptEnabled ? (
        <AiAnswersVerifiedAnswersSection
          loading={aiAnswersLoading}
          rows={aiAnswers}
          organizationId={selectedOrganizationId}
          dateRange={dateRange}
        />
      ) : (
        <AiAnswersSearchLog loading={aiAnswersLoading} rows={aiAnswers} dateRange={dateRange} />
      )}
      {/* Outside the agentGptEnabled gate: the API checks access by connection, whatever org is selected. */}
      {!isNil(reviewConnectionId) && (
        <ReviewFilesModal
          key={reviewConnectionId}
          connectionId={reviewConnectionId}
          onClose={closeReview}
        />
      )}
    </>
  )
}
